import { computed, getCurrentInstance, onUnmounted, reactive, ref, type Ref } from 'vue'
import Network from '@/Network'
import { CompanyDoctorInviteStatusEnum } from '@/Network/Types/Requests/CompanyRequests'
import type { DoctorGroupedResponse } from '@/Network/Types/Responses/CompanyResponses'
import { useErrorToast } from '@/composables/useErrorToast'

/**
 * web#3352 — busca do campo "Médico" do filtro de Relatórios.
 *
 * Antes o modal carregava 1000 médicos uma vez e filtrava no navegador, e toda empresa
 * passa de 1000. Agora cada termo vai a `GET /companies/doctor?search=` com parâmetros
 * próprios: nada do `doctorQuery` compartilhado (busca da tela de Médicos, empresa e
 * status da grade) entra aqui, e nada daqui volta para o store.
 *
 * Mesma política do autocomplete da folha (`useDoctorSuggestions`, #2231): debounce,
 * mínimo de caracteres e "a última resposta ganha", porque o wrapper de request não
 * cancela o `get`.
 */

export const DOCTOR_FILTER_DEBOUNCE_MS = 300
export const DOCTOR_FILTER_MIN_SEARCH_LENGTH = 3
export const DOCTOR_FILTER_PAGE_LIMIT = 20
export const DOCTOR_FILTER_SEARCH_ERROR = 'Não foi possível buscar os médicos.'

const MIN_LENGTH_TEXT = 'Digite ao menos 3 letras do nome ou o CRM'
const NO_RESULTS_TEXT = 'Nenhum médico encontrado'

export type DoctorFilterOption = { title: string; subtitle: string; value: string }

/**
 * Tira os acentos do termo. Os nomes de CRM vêm quase todos sem acento (95% em PROD) e o
 * backend não tem `unaccent`: sem isso, "joão" acharia 13 dos 84 João/Joao (03/11/2026).
 */
export function normalizeDoctorSearchTerm(term: string): string {
  return term
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
}

const toOption = ({ doctor }: DoctorGroupedResponse): DoctorFilterOption => ({
  title: doctor.mainCrm.name,
  subtitle: `${doctor.mainCrm.number}-${doctor.mainCrm.uf}`,
  value: String(doctor.id)
})

export function useDoctorFilterSearch(selectedIds: Ref<string[] | undefined>) {
  const results = ref<DoctorFilterOption[]>([])
  const total = ref(0)
  const isSearching = ref(false)
  const hasSearched = ref(false)
  /**
   * Toda opção já recebida nesta instância. Um id selecionado que sai de `items` vira, no
   * Vuetify, um chip com o id cru — é daqui que o chip tira o nome quando a busca muda.
   */
  const knownOptions = reactive(new Map<string, DoctorFilterOption>())

  const { notifyError } = useErrorToast()
  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  let latestRequestId = 0

  const options = computed(() => {
    const ids = selectedIds.value ?? []
    const selected = ids.flatMap((id) => knownOptions.get(id) ?? [])
    return [...selected, ...results.value.filter((option) => !ids.includes(option.value))]
  })

  const noDataText = computed(() => (hasSearched.value ? NO_RESULTS_TEXT : MIN_LENGTH_TEXT))

  const overflowText = computed(() =>
    total.value > results.value.length
      ? `Mostrando ${results.value.length} de ${total.value}. Refine a busca.`
      : ''
  )

  function reset() {
    if (debounceTimer) clearTimeout(debounceTimer)
    debounceTimer = null
    latestRequestId += 1
    results.value = []
    total.value = 0
    hasSearched.value = false
    isSearching.value = false
  }

  async function fetchDoctors(term: string) {
    const requestId = (latestRequestId += 1)
    isSearching.value = true

    try {
      const { data, count } = await Network.company.getCompanyDoctors({
        search: term,
        inviteStatus: CompanyDoctorInviteStatusEnum.ACCEPTED,
        limit: DOCTOR_FILTER_PAGE_LIMIT,
        offset: 0
      })
      if (requestId !== latestRequestId) return

      const found = data.filter((row) => row.doctor?.mainCrm).map(toOption)
      found.forEach((option) => knownOptions.set(option.value, option))
      results.value = found
      total.value = count
      hasSearched.value = true
    } catch (error) {
      if (requestId !== latestRequestId) return
      console.error('Erro ao buscar médicos para o filtro:', error)
      results.value = []
      total.value = 0
      hasSearched.value = false
      notifyError(error, DOCTOR_FILTER_SEARCH_ERROR)
    } finally {
      if (requestId === latestRequestId) isSearching.value = false
    }
  }

  function search(term: string | null) {
    if (debounceTimer) clearTimeout(debounceTimer)

    const normalized = normalizeDoctorSearchTerm(term ?? '')
    if (normalized.length < DOCTOR_FILTER_MIN_SEARCH_LENGTH) {
      reset()
      return
    }

    debounceTimer = setTimeout(() => {
      debounceTimer = null
      void fetchDoctors(normalized)
    }, DOCTOR_FILTER_DEBOUNCE_MS)
  }

  if (getCurrentInstance()) {
    onUnmounted(reset)
  }

  return { options, isSearching, noDataText, overflowText, search, reset }
}
